import React, { useState, useEffect, useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  Box,
  Typography,
  Grid,
  IconButton,
  Divider,
  Paper,
  Avatar,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { sendWebSocketMessage } from "../../hooks/useWebSocket";
import { useSelector, useDispatch } from "react-redux";
import {
  updateLoading,
  updateLoadingMsg,
  addActiveRequests,
} from "../../Redux/Reducers/uiSlice";
import { investArr } from "../../component/Methods";
import { useTradeBlotterState } from "../../hooks/useOptimizedState";
import {
  getSmaBucketStrategyLabel,
  isFundSma,
  buildTickerDropdownOptions,
  TEMP_TICKER_DROPDOWN_ROWS,
} from "./utility";
import {
  getActionAndPosition,
  getTradeActionOptions,
  sortFundsByAbbreviation,
  sortBrokersByAbbreviation,
  sortStrategiesForOrderDialog,
  findStrategyByFormValue,
  getFundAccountNames,
  getInvestmentTypeFromAssetType,
  applyAssetTypeToForm,
  validateOptionFormFields,
  buildOptionFieldsForOrder,
  buildOrderTickerPayload,
  buildDistributionFromRows,
  resolveRoutingEnum,
} from "./orderDialogHelpers";
import { useSmaBucketStrategySync } from "./hooks/useSmaBucketStrategySync";
import TickerSymbolAutocomplete from "./components/TickerSymbolAutocomplete";
import OrderOptionFieldsSection from "./components/OrderOptionFieldsSection";



const NewOrderDialog = ({ open, onClose, onSubmit }) => {
  const { funds, strategies, brokers, lots, managers } = useTradeBlotterState();
  const user = useSelector((state) => state?.users?.user);

  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    fund: "",
    strategy: "",
    action: "",
    ticker: "",
    stock: "",
    option: 1, // Options enum: calls=0, puts=1 (default puts)
    strikePrice: "",
    expirationDate: "", // YYYY-MM-DD
    broker: "",
    routing: "Manual", // Default to Manual
    assetType: "Equity", // Default to Equity
  });

  const [distributionRows, setDistributionRows] = useState([
    { account: "", shareCount: "" },
  ]);

  const [availableAccounts, setAvailableAccounts] = useState([]);

  const sortedStrategies = useMemo(
    () => sortStrategiesForOrderDialog(strategies, { includeId: true }),
    [strategies]
  );

  const selectedFundMeta = useMemo(
    () => funds?.find((f) => f.abbreviation === formData.fund),
    [funds, formData.fund]
  );
  const isSmaSelectedFund = isFundSma(selectedFundMeta?.fundId, managers);
  const smaBucketLabel = useMemo(
    () => getSmaBucketStrategyLabel(strategies),
    [strategies]
  );

  useSmaBucketStrategySync({
    formData,
    setFormData,
    selectedFundMeta,
    managers,
    strategies,
  });

  useEffect(() => {
    setAvailableAccounts(getFundAccountNames(funds, formData.fund));
  }, [formData.fund, funds]);

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleDistributionChange = (field) => (event) => {
    setDistributionRows((prev) =>
      prev.map((row, i) =>
        i === 0 ? { ...row, [field]: event.target.value } : row
      )
    );
  };

  const handleSubmit = () => {
    // Validate required fields
    if (
      !formData.fund ||
      !formData.action ||
      !formData.ticker ||
      !formData.broker
    ) {
      alert("Please fill in all required fields");
      return;
    }

    // Validate distribution rows
    const hasEmptyDistribution = distributionRows.some(
      (row) => !row.account || !row.shareCount
    );
    if (hasEmptyDistribution) {
      alert("Please fill in all distribution account and share count fields");
      return;
    }

    // Find the selected fund to get fundId
    const selectedFund = funds.find(
      (fund) => fund.abbreviation === formData.fund
    );
    if (!selectedFund?.fundId) {
      alert("Invalid fund selected");
      return;
    }

    const isSMA = isFundSma(selectedFund.fundId, managers) ? 1 : 0;

    let selectedStrategy = null;
    if (isSMA !== 1 && formData.strategy) {
      selectedStrategy = findStrategyByFormValue(strategies, formData.strategy);
    }

    if (isSMA !== 1) {
      if (!formData.strategy) {
        alert("Please select a strategy");
        return;
      }
      if (!selectedStrategy) {
        alert("Invalid strategy selected");
        return;
      }
    }

    const distribution = buildDistributionFromRows({
      distributionRows,
      selectedFund,
      isSMA,
      selectedStrategy,
    });

    const { action: actionValue, position } = getActionAndPosition(
      formData.action
    );

    // Get broker ID
    const broker = brokers?.find((b) => b.abbreviation === formData.broker);

    if (!broker?.brokerId) {
      alert("Invalid broker selected");
      return;
    }
    const brokerId = broker.brokerId;

    const investmentType = getInvestmentTypeFromAssetType(formData.assetType);
    if (!validateOptionFormFields(formData, investmentType)) return;

    const optionFields = buildOptionFieldsForOrder(formData, investmentType);

    const orderData = {
      distribution,
      action: actionValue,
      position: position,
      broker: brokerId,
      type: 1,
      typePrice: 0,
      instructions: "best way",
      routing: resolveRoutingEnum(formData.routing),
      ticker: buildOrderTickerPayload(formData, investmentType, optionFields),
      user: user?.username || user?.email,
    };

    const requestData = {
      action: "new_order",
      data: {
        orders: [orderData],
      },
    };

    dispatch(updateLoading(true));
    dispatch(updateLoadingMsg("Submitting trade order..."));
    dispatch(addActiveRequests("new_order"));

    sendWebSocketMessage(JSON.stringify(requestData));
    onSubmit(requestData);
    handleClose();
  };

  const handleClose = () => {
    setFormData({
      fund: "",
      strategy: "",
      action: "",
      ticker: "",
      stock: "",
      option: 1,
      strikePrice: "",
      expirationDate: "",
      broker: "",
      routing: "Manual",
      assetType: "Equity",
    });
    setDistributionRows([{ account: "", shareCount: "" }]);
    setAvailableAccounts([]);
    onClose();
  };

  const uniqueTickers = React.useMemo(
    () =>
      buildTickerDropdownOptions({
        lots,
        extraRows: TEMP_TICKER_DROPDOWN_ROWS,
      }),
    [lots]
  );

  const sortedFunds = useMemo(
    () => sortFundsByAbbreviation(funds, { requireAbbreviation: true }),
    [funds]
  );

  const sortedBrokers = useMemo(
    () => sortBrokersByAbbreviation(brokers, { requireAbbreviation: true }),
    [brokers]
  );

  const investmentType = getInvestmentTypeFromAssetType(formData.assetType);
  const showOptionFields = investmentType === 10;
  const actionOptions = getTradeActionOptions(showOptionFields);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: "80vh",
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Avatar
            sx={{
              bgcolor: "primary.main",
              width: 48,
              height: 48,
            }}
          >
            <AddIcon />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" component="h2" sx={{ fontWeight: 700 }}>
              New Order
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create a new trading order
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{
              color: "text.secondary",
              "&:hover": { color: "text.primary" },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1, pb: 2, overflowY: "visible" }}>
        <Grid container spacing={1.5}>
          {/* Fund Dropdown */}
          <Grid size={{ xs: 12 }}>
            <Autocomplete
              options={sortedFunds}
              getOptionLabel={(option) => option.abbreviation}
              value={
                sortedFunds.find(
                  (fund) => fund.abbreviation === formData.fund
                ) || null
              }
              onChange={(event, newValue) => {
                const abbr = newValue?.abbreviation || "";
                const newFund = funds?.find((f) => f.abbreviation === abbr);
                const nextSma = isFundSma(newFund?.fundId, managers);
                setFormData((prev) => ({
                  ...prev,
                  fund: abbr,
                  ...(!nextSma ? { strategy: "" } : {}),
                }));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Fund"
                  variant="outlined"
                  InputLabelProps={{ shrink: true, sx: { fontWeight: 600 } }}
                  size="small"
                  required
                />
              )}
            />
          </Grid>

          {/* Strategy: required for non-SMA; SMA shows fixed CPFactorBW bucket (read-only). */}
          {(sortedStrategies.length > 0 || isSmaSelectedFund) && (
            <Grid size={{ xs: 12 }}>
              {isSmaSelectedFund ? (
                <TextField
                  fullWidth
                  label="Strategy"
                  value={smaBucketLabel}
                  disabled
                  size="small"
                  InputLabelProps={{ shrink: true, sx: { fontWeight: 600 } }}
                />
              ) : (
                <Autocomplete
                  options={sortedStrategies}
                  getOptionLabel={(option) =>
                    option.name || option.strategyId || option.id || ""
                  }
                  value={
                    sortedStrategies.find(
                      (strategy) =>
                        strategy.name === formData.strategy ||
                        strategy.strategyId === formData.strategy ||
                        strategy.id === formData.strategy
                    ) || null
                  }
                  onChange={(event, newValue) => {
                    setFormData({
                      ...formData,
                      strategy:
                        newValue?.name ||
                        newValue?.strategyId ||
                        newValue?.id ||
                        "",
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Strategy"
                      variant="outlined"
                      size="small"
                      required
                      InputLabelProps={{ shrink: true, sx: { fontWeight: 600 } }}
                    />
                  )}
                />
              )}
            </Grid>
          )}

          {/* Asset Type Dropdown */}
          <Grid size={{ xs: 12 }}>
            <Autocomplete
              options={investArr}
              value={formData.assetType}
              onChange={(event, newValue) => {
                setFormData((prev) =>
                  applyAssetTypeToForm(prev, newValue || "Equity")
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Asset Type"
                  variant="outlined"
                  size="small"
                  InputLabelProps={{ shrink: true, sx: { fontWeight: 600 } }}
                  required
                />
              )}
            />
          </Grid>

          {/* Action Dropdown */}
          <Grid size={{ xs: 12 }}>
            <Autocomplete
              options={actionOptions}
              getOptionLabel={(option) =>
                typeof option === "string" ? option : option.label
              }
              value={
                actionOptions.find((o) =>
                  typeof o === "string"
                    ? o === formData.action
                    : o.value === formData.action
                ) || null
              }
              onChange={(event, newValue) => {
                setFormData({
                  ...formData,
                  action:
                    typeof newValue === "string"
                      ? newValue
                      : newValue?.value || "",
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Action"
                  variant="outlined"
                  size="small"
                  InputLabelProps={{ shrink: true, sx: { fontWeight: 600 } }}
                  required
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TickerSymbolAutocomplete
              uniqueTickers={uniqueTickers}
              ticker={formData.ticker}
              stock={formData.stock}
              setFormData={setFormData}
            />
          </Grid>

          {showOptionFields && (
            <OrderOptionFieldsSection formData={formData} setFormData={setFormData} />
          )}

          {/* Broker Dropdown */}
          <Grid size={{ xs: 12 }}>
            <Autocomplete
              options={sortedBrokers}
              getOptionLabel={(option) => option.abbreviation}
              value={
                sortedBrokers.find(
                  (broker) => broker.abbreviation === formData.broker
                ) || null
              }
              onChange={(event, newValue) => {
                setFormData({
                  ...formData,
                  broker: newValue?.abbreviation || "",
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Broker"
                  variant="outlined"
                  size="small"
                  InputLabelProps={{ shrink: true, sx: { fontWeight: 600 } }}
                  required
                />
              )}
            />
          </Grid>

          {/* Routing Dropdown */}
          <Grid size={{ xs: 12 }}>
            <Autocomplete
              options={["Manual", "Auto"]}
              value={formData.routing}
              onChange={(event, newValue) => {
                setFormData({
                  ...formData,
                  routing: newValue || "Manual",
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Routing"
                  variant="outlined"
                  size="small"
                  InputLabelProps={{ shrink: true, sx: { fontWeight: 600 } }}
                  required
                />
              )}
            />
          </Grid>



          {/* Account and Share Count Row */}
          <Grid size={{ xs: 6 }}>
            <Autocomplete
              options={availableAccounts}
              value={distributionRows[0]?.account || null}
              onChange={(event, newValue) => {
                handleDistributionChange("account")({
                  target: { value: newValue || "" },
                });
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Account"
                  variant="outlined"
                  size="small"
                  InputLabelProps={{ shrink: true, sx: { fontWeight: 600 } }}
                  required
                />
              )}
            />
          </Grid>
          <Grid size={{ xs: 6 }}>
            <TextField
              fullWidth
              label="Share Count"
              type="number"
              value={distributionRows[0]?.shareCount || ""}
              onChange={handleDistributionChange("shareCount")}
              variant="outlined"
              size="small"
              InputLabelProps={{ shrink: true, sx: { fontWeight: 600 } }}
              required
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 1, justifyContent: "flex-end" }}>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          sx={{
            minWidth: 120,
            px: 3,
            textTransform: "none",
            fontWeight: 600,
          }}
        >
          Create Order
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewOrderDialog;
